import { API_BASE, authHeaders } from './apiAuth'
import type { ImageRef } from './imageRefs'
import { encodeRef } from './imageRefs'

/**
 * Hand-off between the website and the Miro panel
 * -------------------------------------------------------------
 * The standalone website cannot write to a Miro board — only code running
 * inside Miro can. So "Send to Miro" on the website parks a selection in a
 * small queue, and the WonderWorks panel picks it up the next time it is open
 * on a board.
 *
 * The queue cannot live in localStorage: the panel runs in an iframe inside
 * miro.com, and browsers partition iframe storage by the top-level site, so the
 * website and the panel would each see a different, empty store.
 *
 * WHOSE QUEUE, AND HOW THAT IS ESTABLISHED
 * Every queue belongs to one person, so two designers working at the same time
 * never see each other's images.
 *
 * This used to be asserted rather than proved. The website sent its email in a
 * header and the panel sent its Miro id in another, and the dev server believed
 * both — which was survivable only because that server listened on one laptop's
 * localhost and nobody else could reach it.
 *
 * Now each side sends something it cannot have forged, and the Worker checks it:
 *
 *   website — the Supabase access token, issued after Microsoft signed the
 *             person in. Sent as `Authorization: Bearer`.
 *   panel   — `miro.board.getIdToken()`, signed by Miro with this app's client
 *             secret. Sent as `X-WW-Miro-Token`.
 *
 * Two different headers rather than one, so the Worker never has to guess which
 * verifier a token belongs to.
 */

export interface MiroQueue {
  /**
   * Each one names a picture and the store it is in: `library:…`, `project:…`.
   * Not ids — anything that can sit on a canvas can be sent to a board, so
   * "which store" travels with "which one". A bare id still means the library,
   * which is what everything queued before this looks like.
   */
  items: string[]
  /** ISO timestamp of when the selection was sent, empty when the queue is empty */
  createdAt: string
  /**
   * ISO timestamp of when the panel actually put this selection on a board.
   * Empty unless that has happened. The website watches this to know when its
   * own selection has been used up.
   */
  placedAt: string
  /** True when the panel has proved who it is but nobody has linked it yet */
  unidentified?: boolean
  /**
   * The request did not get an answer — refused, or never arrived.
   *
   * Without this, a failure and an empty queue were the same value, and the
   * panel drew the same nothing for both. That cost an evening: a single
   * refused request made images sent from the website silently never appear,
   * with no error anywhere to say so. A caller can now tell "there is nothing
   * waiting" from "I could not find out".
   */
  failed?: boolean
  /**
   * The newest frame the server has filled for this person, or is filling.
   * Only in answers to the website, which follows what it sent until it is
   * on the board and then lets its message go.
   */
  fill?: MiroFill
}

export interface MiroFill {
  status: 'queued' | 'running' | 'done' | 'failed'
  total: number
  placed: number
  failed: number
  error: string
  createdAt: string
}

export interface MiroLink {
  linked: boolean
  email: string
  /**
   * The panel could not prove who it is, which is a different problem from not
   * being linked and has a different answer.
   *
   * Without this the two were indistinguishable: a refused token produced
   * `linked: false`, the panel offered to connect the account, connecting
   * succeeded — and the panel still said not connected, because the refusal
   * was never about the link. Anything that sends somebody round a loop needs
   * to be able to say why.
   */
  unverified?: boolean
}

/**
 * Which surface is asking. It no longer carries an identity — only a statement
 * of which kind of proof to attach.
 */
export type QueueOwner = 'web' | 'panel'

/**
 * Where the backend lives. Empty means same origin, which is what a deployed
 * build uses when the Worker sits behind the same domain.
 */
const QUEUE_ENDPOINT = `${API_BASE}/api/miro-queue`
const LINK_ENDPOINT = `${API_BASE}/api/miro-link`
const EMPTY: MiroQueue = { items: [], createdAt: '', placedAt: '' }

function normalise(value: unknown): MiroQueue {
  if (!value || typeof value !== 'object') return EMPTY
  const raw = value as Partial<MiroQueue>
  const items = Array.isArray(raw.items)
    ? raw.items.filter((entry): entry is string => typeof entry === 'string')
    : []
  return {
    items,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    placedAt: typeof raw.placedAt === 'string' ? raw.placedAt : '',
    unidentified: raw.unidentified === true,
    fill: raw.fill && typeof raw.fill === 'object' && typeof raw.fill.status === 'string' ? raw.fill : undefined,
  }
}

/* ---------- Linking a Miro account to a WonderWorks account ---------- */

/** Has this panel's Miro account been linked to someone's WonderWorks account? */
export async function readMiroLink(): Promise<MiroLink> {
  try {
    const response = await fetch(LINK_ENDPOINT, {
      headers: { Accept: 'application/json', ...(await authHeaders('panel')) },
    })
    // Any answer that is not an answer means the same thing: we did not find
    // out. A refused token, a server error, a request that never arrived — none
    // of them is "nobody has linked this panel", and reporting them as that is
    // what made the panel offer to connect an account that was already
    // connected, half a second before correcting itself.
    if (!response.ok) return { linked: false, email: '', unverified: true }
    const raw = (await response.json()) as Partial<MiroLink>
    return { linked: raw.linked === true, email: typeof raw.email === 'string' ? raw.email : '' }
  } catch {
    // Never reached the Worker at all — same practical answer as a refusal.
    return { linked: false, email: '', unverified: true }
  }
}

/**
 * Disconnect, from the panel: the link and the server's Miro access together,
 * since one Connect Miro made both. The queue is left alone: it belongs to the
 * email, so anything already waiting is still there after connecting again.
 */
export async function unlinkMiroAccount(): Promise<void> {
  try {
    await fetch(LINK_ENDPOINT, { method: 'DELETE', headers: await authHeaders('panel') })
  } catch {
    /* nothing useful to do here; the panel will simply still look linked */
  }
}

/* ---------- The queue itself ---------- */

/**
 * Park a selection for the Miro panel to pick up. Adds to whatever is waiting.
 *
 * Takes pictures rather than library ids: anything that can sit on a canvas —
 * a library asset, a file dropped on a board, something a skill made — can be
 * sent, and the panel is told which store to fetch each one from.
 */
export async function sendToMiroQueue(
  owner: QueueOwner,
  refs: ImageRef[],
): Promise<MiroQueue> {
  const response = await fetch(QUEUE_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders(owner)) },
    body: JSON.stringify({ items: refs.map(encodeRef) }),
  })
  if (!response.ok) {
    throw new Error(`Could not queue the selection (HTTP ${response.status})`)
  }
  return normalise(await response.json())
}

/** Read whatever is waiting. Never throws — a missing endpoint means "nothing". */
export async function readMiroQueue(owner: QueueOwner): Promise<MiroQueue> {
  try {
    const response = await fetch(QUEUE_ENDPOINT, {
      headers: { Accept: 'application/json', ...(await authHeaders(owner)) },
    })
    if (!response.ok) return { ...EMPTY, failed: true }
    return normalise(await response.json())
  } catch {
    return { ...EMPTY, failed: true }
  }
}

/**
 * Take these out of the queue, and nothing else: the panel claiming the
 * pictures it is about to place. Whatever the website sends meanwhile stays
 * waiting. Throws if the server did not agree, so the caller knows the
 * pictures may still be offered again.
 */
export async function takeFromMiroQueue(owner: QueueOwner, items: string[]): Promise<void> {
  const response = await fetch(QUEUE_ENDPOINT, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders(owner)) },
    body: JSON.stringify({ items }),
  })
  if (!response.ok) throw new Error(`Could not take them from the queue (HTTP ${response.status})`)
}

/**
 * Say that what was taken is now on a board, without touching anything else
 * waiting in the queue. The website's note about a send goes when it sees
 * this (useMiroSendProgress). A batch is also followed through the server's
 * fill; one picture, placed by the panel itself, is known only through this.
 */
export async function markPlacedOnMiro(owner: QueueOwner): Promise<void> {
  const response = await fetch(QUEUE_ENDPOINT, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders(owner)) },
    body: JSON.stringify({ placed: true }),
  })
  if (!response.ok) throw new Error(`Could not mark them placed (HTTP ${response.status})`)
}

/**
 * Empty the queue.
 *
 * `placed` distinguishes the two reasons it can empty. Put on a board, the
 * images have been used and the website should let go of them. Discarded, the
 * person decided against these images for now — their selection on the website
 * is left exactly as it was, because they may well still want it.
 */
export async function clearMiroQueue(
  owner: QueueOwner,
  options: { placed?: boolean } = {},
): Promise<void> {
  try {
    await fetch(QUEUE_ENDPOINT, {
      method: 'DELETE',
      headers: {
        ...(await authHeaders(owner)),
        ...(options.placed ? { 'X-WW-Placed': '1' } : {}),
      },
    })
  } catch {
    /* the queue is a convenience; failing to clear it is not worth an error */
  }
}
